import { useRef } from 'react';
import { Avatar, Badge, Text, Tooltip } from '@chakra-ui/react';
import { t } from '@transifex/native';
import { format } from 'date-fns';
import { useIsOverflowing } from 'features/common/hooks/useIsOverflowing';
import type {
  DataTableRowAction,
  DataTableRowActionCallback,
} from 'features/common/components/DataTable';
import { OrgGroupMember } from 'features/user-group/user-group-tabs/api/useOrgGroupMembersList';
import { OrganizationMember, OrganizationMemberAction } from 'gql/graphql';
import { Link } from 'react-router-dom';
import { history } from '../../../common/history/history';

const TruncatedGroupName = ({ name }: { name: string }) => {
  const groupNameRef = useRef<HTMLParagraphElement | null>(null);
  const isOverflowing = useIsOverflowing(groupNameRef, 'horizontal', name);
  return (
    <Tooltip label={name} placement='top' isDisabled={!isOverflowing}>
      <Text ref={groupNameRef} size='sm' isTruncated maxWidth='200px'>
        {name}
      </Text>
    </Tooltip>
  );
};

export interface IMemberRowDataItem {
  id: string;
  givenName: string;
  familyName: string;
  email: string;
  lastLogin?: string | null;
  orgRole: string;
  picture?: string | null;
  groups?: Array<{ id: string; name: string }> | null;
  actions?: OrganizationMemberAction[] | null;
}

const normalizeGroups = (
  groups: unknown,
): Array<{ id: string; name: string }> | null => {
  if (!groups) return null;

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

  const normalizeGroupArray = (
    arr: unknown[],
  ): Array<{ id: string; name: string }> | null => {
    if (arr.length === 0) return [];

    const normalized = arr
      .map((group) => {
        if (!isRecord(group)) return null;
        const id = group.id;
        const name = group.name;
        if (typeof id !== 'string' || typeof name !== 'string') return null;
        return { id, name };
      })
      .filter((g): g is { id: string; name: string } => Boolean(g));

    return normalized.length > 0 ? normalized : null;
  };

  if (Array.isArray(groups)) {
    return normalizeGroupArray(groups);
  }

  if (isRecord(groups) && Array.isArray(groups.nodes)) {
    return normalizeGroupArray(groups.nodes);
  }

  return null;
};

type ColumnVisibility = {
  access?: boolean;
  familyName?: boolean;
  email?: boolean;
  teams?: boolean;
  lastLogin?: boolean;
};

type AllowMemberAccess = (
  memberId: string | undefined,
  organizationRole: string,
) => boolean;

type GetTableOptionsArgs = {
  page: number;
  allowMemberAccess?: AllowMemberAccess;
  hideColumn?: ColumnVisibility;
  showGroupsColumn?: boolean;
  rowActions?:
    | DataTableRowAction<IMemberRowDataItem>[]
    | DataTableRowActionCallback<IMemberRowDataItem>;
};

export const transformDataForTable = (
  data?: OrganizationMember[] | null,
): IMemberRowDataItem[] =>
  data?.map((member) => {
    const {
      id,
      givenName,
      familyName,
      email,
      organizationRoles,
      lastLogin,
      picture,
      appearance,
    } = member;

    return {
      id,
      givenName: givenName ?? '',
      familyName: familyName ?? '',
      email,
      orgRole: organizationRoles?.nodes?.[0]?.name || 'No Role',
      lastLogin,
      picture,
      groups:
        normalizeGroups((member as unknown as { groups?: unknown }).groups) ||
        null,
      actions: appearance?.memberActions?.nodes,
    };
  }) ?? [];

export const useRowActions = (
  page: number,
  onRemove?: (member: OrgGroupMember) => void,
) => {
  return ((memberRow: { original: any }) => {
    const { actions, id } = memberRow.original as IMemberRowDataItem;

    if (actions && Array.isArray(actions) && actions.length > 0) {
      return actions.map(
        ({ name, displayName, link }, i): DataTableRowAction => ({
          id: String(i),
          label: displayName ?? name ?? '',
          onClick: () => {
            history.push(
              `/admin/members/page/${page}/${link?.to}${history.location.search}`,
            );
          },
          'data-behavior-analytics-id': `${(
            name ?? 'action'
          ).toLowerCase()} - submenu`,
        }),
      );
    }

    return [
      {
        id: '0',
        label: t('View details'),
        onClick: () => {
          history.push(
            `/admin/members/page/${page}/details/${id}${history.location.search}`,
          );
        },
        'data-behavior-analytics-id': 'view details - submenu',
      },
      {
        id: '1',
        label: t('Remove from group'),
        onClick: () => {
          if (onRemove) onRemove(memberRow.original);
        },
        'data-behavior-analytics-id': 'remove from group - submenu',
      },
    ];
  }) as DataTableRowActionCallback<IMemberRowDataItem>;
};

export const getTableOptions = (
  {
    page,
    hideColumn = {},
    allowMemberAccess,
    showGroupsColumn = false,
    rowActions,
  }: GetTableOptionsArgs = { page: 1 },
) => {
  const { access, familyName, email, teams, lastLogin } = hideColumn;

  const getRowActions =
    rowActions ||
    (((memberRow) => {
      const { actions, id, orgRole } = memberRow.original as IMemberRowDataItem;

      const actionItems =
        actions?.map(
          ({ name, displayName, link }, i): DataTableRowAction => ({
            id: String(i),
            label:
              (displayName ?? name) === 'Remove access'
                ? t('Remove user')
                : displayName ?? name ?? '',
            onClick: () => {
              history.push(
                `/admin/members/page/${page}/${link?.to}${history.location.search}`,
              );
            },
            'data-behavior-analytics-id': `${(
              name ?? 'action'
            ).toLowerCase()} - submenu`,
          }),
        ) || [];

      if (actionItems.length === 0) {
        actionItems.push({
          id: String(actionItems.length),
          label: t('View details'),
          onClick: () => {
            history.push(
              `/admin/members/page/${page}/details/${id}${history.location.search}`,
            );
          },
          'data-behavior-analytics-id': 'view details - submenu',
        });
      }

      const hasDeleteOrRemoveAction = actionItems.some(
        (item) =>
          item.label?.toLowerCase().includes('remove') ||
          item.label?.toLowerCase().includes('delete'),
      );

      const canShowDelete =
        !allowMemberAccess || allowMemberAccess(memberRow.original.id, orgRole);

      if (!hasDeleteOrRemoveAction && canShowDelete) {
        actionItems.push({
          id: String(actionItems.length),
          label: t('Remove user'),
          onClick: () => {
            history.push(
              `/admin/members/page/${page}/remove/${id}${history.location.search}`,
            );
          },
          'data-behavior-analytics-id': 'remove user - submenu',
        });
      }

      return actionItems;
    }) as DataTableRowActionCallback<IMemberRowDataItem>);

  const allColumns = [
    {
      Header: '',
      id: 'avatar',
      accessor: (user: any) => (
        <Link
          to={`/admin/members/page/${page}/details/${user.id}${history.location.search}`}
          data-behavior-analytics-id='view details - avatar'
          onClick={(e) => e.stopPropagation()}
        >
          <Avatar
            size='sm'
            name={`${user.givenName} ${user.familyName}`}
            src={user.picture}
          />
        </Link>
      ),
      width: '1%',
      disableSortBy: true,
    },
    {
      Header: t('Given name'),
      accessor: 'givenName',
      disableSortBy: false,
    },
    {
      Header: t('Family name'),
      accessor: 'familyName',
      disableSortBy: false,
    },
    {
      Header: t('Email'),
      accessor: 'email',
      disableSortBy: false,
    },
    ...(access
      ? []
      : [
          {
            Header: t('Individual access'),
            accessor: 'orgRole',
            disableSortBy: false,
          },
        ]),
    ...(showGroupsColumn
      ? [
          {
            Header: t('Groups'),
            id: 'teams',
            accessor: (user: any) => {
              const groups = user.groups;
              if (!groups?.length) {
                return <Text size='sm'>-</Text>;
              }
              if (groups.length === 1) {
                return <TruncatedGroupName name={groups[0].name} />;
              }
              return (
                <Badge colorScheme='primary' variant='subtle'>
                  {t('{count} groups', { count: groups.length })}
                </Badge>
              );
            },
            disableSortBy: false,
          },
        ]
      : []),
    {
      Header: t('Last login'),
      id: 'lastLogin',
      accessor: (user: any) => {
        if (!user.lastLogin) {
          return <Text size='sm'>-</Text>;
        }
        return format(new Date(user.lastLogin).getTime(), 'MMM d, yyyy');
      },
      disableSortBy: false,
    },
  ];

  const columns = allColumns.filter((col: any) => {
    if (familyName && col.accessor === 'familyName') return false;
    if (email && col.accessor === 'email') return false;
    if (teams && col.id === 'teams') return false;
    if (lastLogin && col.id === 'lastLogin') return false;
    return true;
  });

  return {
    rowActions: getRowActions,
    columns,
  };
};
